import { useCallback } from 'react'
import { deleteProvider, PostFormat, postProvider } from 'sharedKernel/api'
import {ProgramInputs} from "../schemas/inputs";

type SaveOptions = {
    onSuccess?: (data: any) => Promise<void>
    onError?: (err: any) => Promise<void>
}

type PayloadFactory<T = ProgramInputs> = (data: ProgramInputs) => any

export const useSaveProgram = <T = any>(payloadFactory?: PayloadFactory<T>, options?: SaveOptions) => {
    const mutation = postProvider(options?.onSuccess, options?.onError)
    const deleteMutation = deleteProvider(options?.onSuccess, options?.onError)

    const fileToBase64 = (file: any): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (!(file instanceof File)) {
            reject(new Error("Invalid file: not a real File object"));
            return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
        });
    };

    const uploadFiles = async (image: any, challengeId: string, type: string) => {
        if (!(image instanceof File)) {
            console.error("Invalid file:", image);
            return;
        }

        const fileContent = await fileToBase64(image);

        const payload = {
            data: {
            Mimetype: image.type,
            Name: image.name,
            size: image.size,
            Type: type,
            Content: fileContent.split(",")[1],
            },
        };

        return mutation.mutateAsync({
            url: `/v1/hub/company/challenge/${challengeId}/file/upload`,
            data: payload,
        });
    };

    const deleteFile = async (
        challengeId: string,
        fileId: string
    ) => {
        return deleteMutation.mutateAsync({
            url: `/v1/hub/company/challenge/${challengeId}/file/${fileId}`
        });
    };

    const save = useCallback(async (data: ProgramInputs, challengeId: string, filesToDelete: string[] = []) => {
        const payload = payloadFactory ? payloadFactory(data) : data
        if (challengeId !== undefined) {
            payload.data.id = challengeId;
            payload.id = challengeId;
        }
        const request: PostFormat = {
            url: '/v1/hub/company/challenge',
            data: payload
        }
        const result = await mutation.mutateAsync(request);
        if (data.coverImage && data.coverImage.length > 0) {
            await uploadFiles(data.coverImage[0], result.data.id, 'coverImage');
        }
        if (data.homePageSlider && data.homePageSlider.length > 0) {
            await uploadFiles(data.homePageSlider[0], result.data.id, 'homePageSlider');
        }
        if (data.supportFiles && data.supportFiles.length > 0) {
            for (const file of data.supportFiles) {
                await uploadFiles(file, result.data.id, 'supportFiles');
            }
        }
        if (data.presentationVideo && data.presentationVideo.length > 0) {
            await uploadFiles(data.presentationVideo[0], result.data.id, 'presentationVideo');
        }

        if (filesToDelete.length > 0) {
            for (const fileId of filesToDelete) {
                await deleteFile(result.data.id, fileId);
            }
        }
        return result;
    }, [mutation, payloadFactory])

    return {
        save,
        mutate: mutation.mutateAsync,
        mutateAsync: mutation.mutateAsync,
        isLoading: mutation.isLoading,
        data: mutation.data,
        error: mutation.error
    }
}

export default useSaveProgram
